import { type ReactNode, useEffect, useState } from 'react'
import { cn } from '../../lib/utils'
import { BottomNavAction } from './BottomNavAction'
import { BottomNavTab } from './BottomNavTab'

export interface BottomNavItem {
  // Stable key for the tab (the app passes the route path).
  id: string
  // Pre-rendered icon (a node so the block stays icon-library agnostic).
  icon: ReactNode
  label: string
  active: boolean
  // Router-agnostic navigation — the app supplies react-router's `<Link>`. See BottomNavTab.
  renderLink: (props: { className: string; 'aria-label': string; children: ReactNode }) => ReactNode
}

export interface BottomNavActionConfig {
  icon: ReactNode
  // Accessible name for the icon-only action button.
  label: string
  onClick?: () => void
}

export interface BottomNavProps {
  items: BottomNavItem[]
  // Scrolled state — the bar shrinks to a centered dots pill and the action button tucks away.
  collapsed: boolean
  // The current page's primary action, shown as the right-hand FAB; `null` hides it (the bar
  // re-expands to full width).
  action: BottomNavActionConfig | null
}

// The mobile bottom navigation as a self-contained, prop-driven block: a floating pill of tabs plus
// a standalone action FAB on the right, over a bottom scrim. It owns the layout and motion — the bar
// squeezes left as the action slides in, both collapse to a dots pill on scroll — and composes
// BottomNavTab / BottomNavAction (which carry the press feel).
//
// Presentation only: `collapsed`, `active`, and `action` are inputs; the app wires scroll, routing,
// and the primary-action slot. It is NOT gated to mobile here — the app wraps it in `md:hidden` (and
// its route guards) so the block stays viewport-agnostic and visible in Storybook.
export function BottomNav({ items, collapsed, action }: BottomNavProps) {
  // Retain the last action so the FAB fades OUT with its icon as the bar re-expands, rather than
  // vanishing instantly when a page clears its action. `display` is the current action when present,
  // else the retained one during the exit.
  const [shownAction, setShownAction] = useState(action)
  useEffect(() => {
    if (action) setShownAction(action)
  }, [action])
  const display = action ?? shownAction
  const showAction = action !== null && !collapsed

  return (
    <>
      <div className="fixed bottom-0 left-0 right-0 z-30 h-20 pointer-events-none bg-gradient-to-t from-background/80 to-60% to-background/0" />
      <div
        className="fixed inset-x-0 z-40 flex items-center justify-center px-4"
        style={{ bottom: 'max(env(safe-area-inset-bottom, 0px), 1rem)' }}>
        <nav
          aria-label="Primary"
          className={cn(
            'flex items-stretch overflow-hidden bg-card-light border border-border rounded-full shadow-medium-shadow transition-all duration-500 ease-spring motion-reduce:transition-none',
            collapsed ? 'h-10 w-[158px] px-1.5' : cn('h-14 px-2', action ? 'w-[calc(100%_-_72px)]' : 'w-full')
          )}>
          {items.map((item) => (
            <BottomNavTab
              key={item.id}
              icon={item.icon}
              label={item.label}
              active={item.active}
              collapsed={collapsed}
              renderLink={item.renderLink}
            />
          ))}
        </nav>
        {/* Action FAB — outside the bar, right side. h-14 matches the bar so this wrapper never grows
            the container (which would shove the bar up and expose the scrim's edge); it leaves ~4px
            around the button for its shadow inside the overflow-hidden clip. */}
        <div
          className={cn(
            'flex h-14 shrink-0 items-center overflow-hidden transition-all duration-500 ease-spring motion-reduce:transition-none',
            showAction ? 'w-[72px] pl-3 opacity-100' : 'w-0 pl-0 opacity-0'
          )}>
          {display && (
            <BottomNavAction icon={display.icon} label={display.label} onClick={display.onClick} tabIndex={showAction ? 0 : -1} />
          )}
        </div>
      </div>
    </>
  )
}
